import React, { useCallback, useState, useEffect } from 'react';
import {
  bool,
  func,
  shape,
  string,
  arrayOf
} from 'prop-types';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
  Typography
} from '@one-thd/sui-atomic-components';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import _isEmpty from 'lodash/isEmpty';
import { useDesignAddTouchCabinet } from '../hooks/cabinetVisualizerHooks';
import { cabinetToBoundingBox,
  createBoundingBoxFromTouchpoint,
  getConstrainedWallSize,
  isCollidingRange } from '../utils';
import { useCabinetVisualizerStore } from '../../../../store/index';
import {
  DEFAULT_WALLSIZE,
  WALL_SEGMENT_HEIGHT,
  selectDesignAnchorSide,
  selectDesignMatrixSizeWithDefault,
  selectDesignProducts
} from '../../../../store/cabinetVisualizer/selectors';
import { EMPTY_OBJECT } from '../../../../utils/empty-reference';
import { CabinetShape } from './type';
import { tryGetQueryParam } from '../../../../utils/queryParams';

const decimalRegex = /^(\d+(\.\d*)?|\.\d+)$/;

const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';

export const SpacerModal = ({
  designId,
  openSpacer,
  closeSpacer,
  touchpoint,
  editMode,
  editSpacerConfig,
  editSpacer,
  isEditCabinetValid,
  cabinets
}) => {

  const [name, setName] = useState(editMode ? editSpacerConfig.name : '');
  const [width, setWidth] = useState(editMode ? editSpacerConfig.width : '');
  const [height, setHeight] = useState(editMode ? editSpacerConfig.height : '');

  const { cabinetVisualizer } = useCabinetVisualizerStore();
  const addTouchPointCabinet = useDesignAddTouchCabinet(designId, cabinets, enableTouchPointsOnBothSides);
  const wallSize = DEFAULT_WALLSIZE; // selectDesignMatrixSizeWithDefault(cabinetVisualizer, designId);
  const anchorPosition = selectDesignAnchorSide(cabinetVisualizer, designId);

  const products = (designId && cabinetVisualizer) ? selectDesignProducts(cabinetVisualizer, designId) : [];
  // All the info the the currently selected cabinet to add
  const cabinetBoundings = products?.map((cab) => cabinetToBoundingBox(cab));
  const cabinetInfo = {
    width: Number(width),
    height: Number(height),
    color: 'grey',
    itemId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
    parentId: touchpoint?.type && touchpoint?.type[0].toLowerCase() === 'wall' ? -2 : -1,
    name,
    productType: touchpoint?.type ? touchpoint.type[0] : 'Wall',
    direction: touchpoint?.direction || 'left',
  };
  const boundingBox = createBoundingBoxFromTouchpoint(touchpoint || {}, {
    ...cabinetInfo,
    maxHeight: wallSize.Rows,
    maxWidth: wallSize.Columns,
    anchorPosition
  });

  const isCabinetColliding = () => {
    return !isEditCabinetValid(cabinetInfo);
  };

  useEffect(() => {
    if (editMode && editSpacerConfig !== EMPTY_OBJECT) {
      setName(editSpacerConfig.name || '');
      setWidth(editSpacerConfig.width || '');
      setHeight(editSpacerConfig.height || '');
    } else {
      setName('');
      setWidth('');
      setHeight('');
    }
  }, [editMode, editSpacerConfig]);

  const addSpacerConfig = useCallback(() => (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeSpacer(event);

    if (editMode) {
      editSpacer(cabinetInfo);
    } else {
      addTouchPointCabinet({
        cabinetInfo,
        boundingBox
      });
    }
  });

  const handleName = (event) => {
    const nameText = event.target.value;
    if (nameText.length <= 20) {
      setName(event.target.value);
    }
  };

  const handleWidth = (event) => {
    if (_isEmpty(event.target.value) || decimalRegex.test(event.target.value)) {
      setWidth(event.target.value);
    }
  };

  const handleHeight = (event) => {
    if (_isEmpty(event.target.value) || decimalRegex.test(event.target.value)) {
      setHeight(event.target.value);
    }
  };

  const fieldValidations = () => {
    if (_isEmpty(String(width)) || _isEmpty(String(height)) || _isEmpty(name)) {
      return true;
    }
    if (Number(width) <= 0 || Number(height) <= 0) {
      return true;
    }
    return false;
  };
  return (
    <>
      <Modal size="sm" open={openSpacer} onClose={closeSpacer}>
        <ModalHeader onClose={closeSpacer}>Add Spacer</ModalHeader>
        <ModalBody>
          <Row className="sui-pl-0 sui-pr-0">
            <Typography variant="body-base" color="primary">
              Spacers can used as placeholders for objects in your room
              (refrigerator, washer, etc.) or to maintain a desired amount
              of space between cabinets.
            </Typography>
          </Row>
          <Row className="sui-pl-0 sui-pr-0">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <form
                className="sui-grid"
                autoComplete="off"
                noValidate
              >
                <TextField
                  required
                  label="Name"
                  id="spacer-name"
                  fullWidth
                  placeholder=""
                  value={name}
                  helpMessage="20 character limit"
                  onChange={handleName}
                />
              </form>
            </Col>
          </Row>
          <Row className="sui-pl-0 sui-pr-0">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <form
                className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4"
                autoComplete="off"
                noValidate
              >
                <TextField
                  required
                  label="Width (in) "
                  id="spacer-width"
                  fullWidth
                  value={width}
                  onChange={handleWidth}
                />
                <TextField
                  required
                  label="Height (in) "
                  id="spacer-height"
                  fullWidth
                  value={height}
                  onChange={handleHeight}
                />
              </form>
            </Col>
          </Row>
          {isCabinetColliding() && openSpacer
          && (
            <Row className="sui-pl-0 sui-pr-0">
              <Col className="sui-pl-0 sui-pr-0" fallback="12">
                <Alert status="error">
                  This spacer in this specified position conflicts with other cabinets or spacers.
                  Please choose a different width/height or remove the obstructing cabinet or spacer.
                </Alert>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter position="end">
          <div className="sui-flex sui-gap-3">
            <Button variant="secondary" onClick={closeSpacer}>Cancel</Button>
            <Button
              variant="primary"
              onClick={addSpacerConfig()}
              disabled={fieldValidations() || isCabinetColliding()}
            >
              {editMode && editSpacerConfig?.width !== undefined ? 'Update Spacer' : 'Add Spacer'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

SpacerModal.propTypes = {
  designId: string.isRequired,
  openSpacer: bool,
  closeSpacer: func.isRequired,
  touchpoint: shape({}),
  editMode: bool,
  editSpacerConfig: shape({}),
  editSpacer: func,
  isEditCabinetValid: func,
  cabinets: arrayOf(CabinetShape)
};

SpacerModal.defaultProps = {
  openSpacer: false,
  touchpoint: null,
  editMode: false,
  editSpacerConfig: {},
  editSpacer: undefined,
  isEditCabinetValid: () => true,
  cabinets: []
};
